/**
 * Admin > Patient Notes: the account's note templates (note_templates) and
 * their categories (notes_types), through the same v5 routes the corporate
 * dashboard's Patient Notes page uses:
 *   /notes            list (shared templates; deleted ones too), show, create,
 *                     update, delete (active = 0), restore
 *   /note-categories  list (with deleted), create, update, delete, restore
 *
 * The admin manages the account's SHARED templates: /notes lists only those
 * (a staff member's own saved templates are theirs), so a new template is
 * created shared, or it would save and never be listed.
 */
import { apiClient } from './apiClient';
import { useAuthStore } from '@/store/authStore';
import type { ApiResponse } from '@/types/api.types';

export interface PatientNoteTemplate {
  id: number;
  name: string;
  subject: string;
  description: string;
  categoryId: number | null;
  hasContent: boolean;
  active: boolean;
}

/** notes_types.isLab is three-way, as corp: none, lab results, lab orders. */
export type NoteCategoryLabs = 0 | 1 | 2;

export const NOTE_CATEGORY_LAB_OPTIONS: { value: NoteCategoryLabs; label: string }[] = [
  { value: 0, label: 'No' },
  { value: 1, label: 'Lab results' },
  { value: 2, label: 'Lab orders' },
];

export interface NoteCategory {
  id: number;
  name: string;
  isLab: NoteCategoryLabs;
  active: boolean;
}

export interface PatientNoteTemplateForm {
  name: string;
  subject: string;
  content: string;
}

type Row = Record<string, unknown>;

const isTrue = (v: unknown) => v === true || v === 1 || v === '1';

/** Corp's placeholder for a template saved with no body (the API treats it as no content). */
const NO_CONTENT = 'No content';

function toTemplate(r: Row): PatientNoteTemplate {
  const categoryId = Number(r.noteTemplateCategoryID ?? 0);
  return {
    id: Number(r.noteTemplateID ?? 0),
    name: String(r.name ?? ''),
    subject: String(r.subject ?? ''),
    description: String(r.description ?? ''),
    categoryId: categoryId > 0 ? categoryId : null,
    hasContent: isTrue(r.hasContent),
    active: r.active === undefined ? true : isTrue(r.active),
  };
}

function toLabs(v: unknown): NoteCategoryLabs {
  const n = Number(v ?? 0);
  return n === 1 || n === 2 ? n : 0;
}

function toCategory(r: Row): NoteCategory {
  return {
    id: Number(r.noteTypeID ?? r.id ?? 0),
    name: String(r.name ?? r.title ?? ''),
    isLab: toLabs(r.isLab),
    active: r.active === undefined ? true : isTrue(r.active),
  };
}

export function listPatientNoteTemplates(): Promise<PatientNoteTemplate[]> {
  return apiClient.get<ApiResponse<Row[]>>('/notes').then((r) => (r.data.data ?? []).map(toTemplate));
}

/** Live and deleted categories (the board shows deleted ones under Show deleted). */
export function listNoteCategories(): Promise<NoteCategory[]> {
  return apiClient
    .get<ApiResponse<Row[]>>('/note-categories', { params: { include_inactive: 1 } })
    .then((r) => (r.data.data ?? []).map(toCategory));
}

/** The template's body, which the list leaves out. Corp's "No content" placeholder reads as empty. */
export function getPatientNoteTemplateContent(id: number): Promise<string> {
  return apiClient.get<ApiResponse<Row>>(`/notes/${id}`).then((r) => {
    const content = String(r.data.data?.content ?? '');
    return content.trim() === NO_CONTENT ? '' : content;
  });
}

export function createPatientNoteTemplate(form: PatientNoteTemplateForm, categoryId: number): Promise<PatientNoteTemplate> {
  const accountId = Number(useAuthStore.getState().accountId ?? 0);
  return apiClient
    .post<ApiResponse<Row>>('/notes', {
      name: form.name.trim(),
      subject: form.subject.trim(),
      // As corp: the columns are never left blank.
      description: 'No description',
      content: form.content.trim() ? form.content : NO_CONTENT,
      accountID: accountId,
      noteTemplateCategoryID: categoryId,
      active: true,
      isShared: true,
    })
    .then((r) => toTemplate({ ...r.data.data, hasContent: form.content.trim() !== '' }));
}

/** Only the fields given are sent. */
export function updatePatientNoteTemplate(
  id: number,
  data: Partial<PatientNoteTemplateForm> & { categoryId?: number },
): Promise<void> {
  const body: Row = {};
  if (data.name !== undefined) body.name = data.name.trim();
  if (data.subject !== undefined) body.subject = data.subject.trim();
  if (data.content !== undefined) body.content = data.content;
  if (data.categoryId !== undefined) body.noteTemplateCategoryID = data.categoryId;
  return apiClient.put(`/notes/${id}`, body).then(() => undefined);
}

export function deletePatientNoteTemplate(id: number): Promise<void> {
  return apiClient.delete(`/notes/${id}`).then(() => undefined);
}

export function restorePatientNoteTemplate(id: number): Promise<void> {
  return apiClient.post(`/notes/${id}/restore`).then(() => undefined);
}

export function createNoteCategory(name: string, isLab: NoteCategoryLabs): Promise<NoteCategory> {
  return apiClient
    .post<ApiResponse<Row>>('/note-categories', { title: name, contains_labs: isLab })
    .then((r) => toCategory({ ...r.data.data, name, isLab }));
}

/** Only the fields given are sent (corp: a Contains Labs nobody touched is never posted). */
export function updateNoteCategory(id: number, data: { name?: string; isLab?: NoteCategoryLabs }): Promise<void> {
  const body: Row = {};
  if (data.name !== undefined) body.title = data.name;
  if (data.isLab !== undefined) body.contains_labs = data.isLab;
  return apiClient.put(`/note-categories/${id}`, body).then(() => undefined);
}

export function deleteNoteCategory(id: number): Promise<void> {
  return apiClient.delete(`/note-categories/${id}`).then(() => undefined);
}

export function restoreNoteCategory(id: number): Promise<void> {
  return apiClient.post(`/note-categories/${id}/restore`).then(() => undefined);
}
